import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import publicGalleryService from "../../features/publicGallery/service/publicGalleryService";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from "../../features/publicGallery/types/publicGalleryTypes";

export const fetchGalleries = createAsyncThunk(
  "publicGallery/fetchAll",
  async (filters, { rejectWithValue }) => {
    try {
      return await publicGalleryService.getGalleries(filters);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchGalleryById = createAsyncThunk(
  "publicGallery/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      return await publicGalleryService.getGalleryById(id);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_ONE_FAILED);
    }
  },
);

export const createGallery = createAsyncThunk(
  "publicGallery/create",
  async ({ data, createdBy }, { rejectWithValue }) => {
    try {
      return await publicGalleryService.createGallery(data, createdBy);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateGallery = createAsyncThunk(
  "publicGallery/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await publicGalleryService.updateGallery(id, data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteGallery = createAsyncThunk(
  "publicGallery/delete",
  async (id, { rejectWithValue }) => {
    try {
      await publicGalleryService.deleteGallery(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

export const archiveGallery = createAsyncThunk(
  "publicGallery/archive",
  async (id, { rejectWithValue }) => {
    try {
      return await publicGalleryService.archiveGallery(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const publishGallery = createAsyncThunk(
  "publicGallery/publish",
  async (id, { rejectWithValue }) => {
    try {
      return await publicGalleryService.publishGallery(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const unpublishGallery = createAsyncThunk(
  "publicGallery/unpublish",
  async (id, { rejectWithValue }) => {
    try {
      return await publicGalleryService.unpublishGallery(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const uploadGalleryImages = createAsyncThunk(
  "publicGallery/uploadImages",
  async ({ galleryId, imagesData }, { rejectWithValue }) => {
    try {
      return await publicGalleryService.uploadImages(galleryId, imagesData);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPLOAD_FAILED);
    }
  },
);

export const deleteGalleryImage = createAsyncThunk(
  "publicGallery/deleteImage",
  async ({ galleryId, imageUrl }, { rejectWithValue }) => {
    try {
      return await publicGalleryService.deleteImage(galleryId, imageUrl);
    } catch (error) {
      return rejectWithValue(
        error.message || ERROR_MESSAGES.IMAGE_DELETE_FAILED,
      );
    }
  },
);

export const toggleImageVisibility = createAsyncThunk(
  "publicGallery/toggleImageVisibility",
  async ({ galleryId, imageUrl }, { rejectWithValue }) => {
    try {
      return await publicGalleryService.toggleImageVisibility(
        galleryId,
        imageUrl,
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const setCoverImage = createAsyncThunk(
  "publicGallery/setCover",
  async ({ galleryId, imageUrl, thumbnailUrl }, { rejectWithValue }) => {
    try {
      return await publicGalleryService.setCoverImage(
        galleryId,
        imageUrl,
        thumbnailUrl,
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateGallerySettings = createAsyncThunk(
  "publicGallery/updateSettings",
  async ({ galleryId, settingsUpdates }, { rejectWithValue }) => {
    try {
      return await publicGalleryService.updateSettings(
        galleryId,
        settingsUpdates,
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchGalleryOverallStats = createAsyncThunk(
  "publicGallery/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      return await publicGalleryService.getOverallStats();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  galleries: [],
  selectedGallery: null,
  stats: null,
  loading: false,
  error: null,
  success: null,
  filters: {
    status: "all",
    category: "all",
    search: "",
  },
};

const upsertGalleryInList = (state, updated) => {
  const idx = state.galleries.findIndex((g) => g.id === updated.id);
  if (idx !== -1) state.galleries[idx] = updated;
  if (state.selectedGallery?.id === updated.id) state.selectedGallery = updated;
};

const publicGallerySlice = createSlice({
  name: "publicGallery",
  initialState,
  reducers: {
    clearErrorState: (state) => {
      state.error = null;
    },
    clearSuccessState: (state) => {
      state.success = null;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedGallery: (state) => {
      state.selectedGallery = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGalleries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGalleries.fulfilled, (state, action) => {
        state.loading = false;
        state.galleries = action.payload;
      })
      .addCase(fetchGalleries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchGalleryById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGalleryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGallery = action.payload;
      })
      .addCase(fetchGalleryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createGallery.fulfilled, (state, action) => {
        state.galleries.unshift(action.payload);
        state.success = SUCCESS_MESSAGES.CREATE_SUCCESS;
      })
      .addCase(createGallery.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateGallery.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.UPDATE_SUCCESS;
      })
      .addCase(updateGallery.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteGallery.fulfilled, (state, action) => {
        state.galleries = state.galleries.filter(
          (g) => g.id !== action.payload,
        );
        state.success = SUCCESS_MESSAGES.DELETE_SUCCESS;
      })
      .addCase(deleteGallery.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(archiveGallery.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
      })
      .addCase(publishGallery.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
      })
      .addCase(unpublishGallery.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
      })

      .addCase(uploadGalleryImages.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.IMAGES_UPLOADED;
      })
      .addCase(uploadGalleryImages.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteGalleryImage.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
        state.success = SUCCESS_MESSAGES.IMAGE_DELETED;
      })
      .addCase(deleteGalleryImage.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(toggleImageVisibility.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
      })

      .addCase(setCoverImage.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
      })

      .addCase(updateGallerySettings.fulfilled, (state, action) => {
        upsertGalleryInList(state, action.payload);
      })

      .addCase(fetchGalleryOverallStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const {
  clearErrorState,
  clearSuccessState,
  updateFilters,
  clearSelectedGallery,
} = publicGallerySlice.actions;

export default publicGallerySlice.reducer;
